import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight, Globe2, PackageCheck } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import SEO from "../components/SEO";
import { CORE_PRODUCTS, ADDITIONAL_SUPPLIES, INDUSTRIES } from "../data/content";

const industryName = (id, lang) => {
  const ind = INDUSTRIES.find((i) => i.id === id);
  return ind ? (lang === "ar" ? ind.ar : ind.en) : id;
};

export default function Products() {
  const { tr, lang } = useLang();

  return (
    <div data-testid="products-page" className="pt-28 sm:pt-36">
      <SEO page="products" lang={lang} />

      <section className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHeading overline={tr.products.badge} title={tr.products.title} desc={tr.products.desc} />
      </section>

      {/* CORE PRODUCTS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
        <Reveal variant="up">
          <div className="flex items-end justify-between gap-4 border-b border-border pb-6">
            <div>
              <h3 className="text-2xl sm:text-3xl font-800 tracking-tight text-ink">{tr.products.coreTitle}</h3>
              <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate">{tr.products.coreDesc}</p>
            </div>
            <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-brand-blue/10 px-3 py-1.5 text-xs font-600 text-brand-blue sm:flex">
              <PackageCheck className="h-3.5 w-3.5" /> {tr.products.inStock}
            </span>
          </div>
        </Reveal>

        <Stagger className="mt-12 grid gap-6 sm:grid-cols-2" stagger={0.08}>
          {CORE_PRODUCTS.map((p) => (
            <StaggerItem key={p.id} variant="up">
              <article
                data-testid={`product-${p.id}`}
                className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-white transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-blue hover:shadow-2xl hover:shadow-brand-blue/10"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-surface">
                  <img src={p.image} alt={p[lang].name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="flex flex-1 flex-col p-6 sm:p-7">
                  <h4 className="text-xl font-800 tracking-tight text-ink">{p[lang].name}</h4>
                  <p className="mt-2.5 text-sm leading-relaxed text-slate">{p[lang].desc}</p>

                  <p className="mt-5 text-[11px] font-700 uppercase tracking-ultra text-brand-blue">{tr.products.specs}</p>
                  <div className="mt-2.5 flex flex-wrap gap-1.5">
                    {p[lang].items.map((it) => (
                      <span key={it} className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-500 text-ink">{it}</span>
                    ))}
                  </div>

                  <div className="mt-auto pt-6">
                    <p className="text-[11px] font-700 uppercase tracking-ultra text-slate/70">{tr.products.industries}</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {p.industries.map((id) => (
                        <span key={id} className="rounded-full bg-brand-red/8 px-2.5 py-1 text-[11px] font-600 text-brand-red">{industryName(id, lang)}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* ADDITIONAL SUPPLIES */}
      <section className="bg-surface py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal variant="up">
            <div className="flex items-end justify-between gap-4 border-b border-border pb-6">
              <div>
                <h3 className="text-2xl sm:text-3xl font-800 tracking-tight text-ink">{tr.products.addTitle}</h3>
                <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate">{tr.products.addDesc}</p>
              </div>
              <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-brand-red/10 px-3 py-1.5 text-xs font-600 text-brand-red sm:flex">
                <Globe2 className="h-3.5 w-3.5" /> {tr.products.sourced}
              </span>
            </div>
          </Reveal>

          <Stagger className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3" stagger={0.08}>
            {ADDITIONAL_SUPPLIES.map((p) => (
              <StaggerItem key={p.id} variant="up">
                <article data-testid={`supply-${p.id}`} className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-white transition-all duration-500 hover:-translate-y-1.5 hover:border-brand-red hover:shadow-xl">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={p.image} alt={p[lang].name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h4 className="text-lg font-800 tracking-tight text-ink">{p[lang].name}</h4>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {p[lang].items.map((it) => (
                        <span key={it} className="rounded-md border border-border bg-surface px-2.5 py-1 text-xs font-500 text-slate">{it}</span>
                      ))}
                    </div>
                  </div>
                </article>
              </StaggerItem>
            ))}
          </Stagger>

          <Reveal variant="scale" className="mt-16">
            <div className="relative overflow-hidden rounded-[2rem] bg-ink px-8 py-12 text-center sm:px-16">
              <div className="absolute -top-20 -right-20 h-60 w-60 rounded-full bg-brand-blue/30 blur-3xl" />
              <p className="relative text-xl sm:text-2xl font-700 text-white">
                {lang === "ar" ? "من جانب واحد إلى قائمة توريد كاملة — نغطيها بالكامل." : "From a single gasket to a full procurement supply list — we cover it all."}
              </p>
              <Link to="/contact" className="relative mt-7 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-700 text-ink transition-all hover:bg-brand-blue hover:text-white">
                {tr.nav.cta} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
